import { createQcsThunks, createResourceEntityAdapter, retryAsync } from 'app-components/flux/core/resourceSliceUtils';
import { getLogger } from 'app-components/utils/core';
import { createAppSlice } from 'app-components/utils/redux-store/createAppSlice';
import type { SetRequired } from 'type-fest';
import { v4 as uuid } from 'uuid';
import buildStorageApi from './api';
import type { DocumentType, StorageDomain } from './types';

const initialState: StorageDomain = {
  entities: {},
  ids: [],
  isLoading: false,
  loadPending: true,
  error: null,
  lastSyncDate: null,
};

const log = getLogger('data/storage/slice');
const entityAdapter = createResourceEntityAdapter<DocumentType>();
export const createStorageSlice = <D extends string>(domain: D) => {
  const api = buildStorageApi(domain);
  const slice = createAppSlice({
    name: `storage/${domain}`,
    initialState: entityAdapter.getInitialState(initialState),
    reducers: (create) => ({
      ...createQcsThunks({
        api,
        create,
        adapter: entityAdapter,
        log,
        refetch(dispatch) {
          dispatch(slice.actions.fetch());
        },
      }),
      syncCdnUrl: create.asyncThunk(
        async (id: string) => {
          return { url: await retryAsync(() => api.getCdnUrl(id, true), 10, 5_000), id };
        },
        {
          fulfilled(state, action) {
            entityAdapter.updateOne(state, { id: action.payload.id, changes: action.payload });
          },
        },
      ),
      syncNoCdnUrl: create.asyncThunk(
        async (id: string) => {
          return { url: await retryAsync(() => api.getCdnUrl(id, false), 10, 5_000), id };
        },
        {
          fulfilled(state, action) {
            entityAdapter.updateOne(state, { id: action.payload.id, changes: action.payload });
          },
        },
      ),
      uploadNewDocument: create.asyncThunk(
        async (
          payload: SetRequired<DocumentType, 'name' | 'mimeType'> & { fileUri: string },
          thunkApi,
        ): Promise<DocumentType> => {
          const clientId = uuid();
          const result = await thunkApi
            .dispatch(
              slice.actions.create({
                name: payload.name,
                mimeType: payload.mimeType,
                clientId,
              }),
            )
            .unwrap();
          const documentId = result.id;
          await retryAsync(() =>
            api.upload({ fileUri: payload.fileUri, name: payload.name, documentId, mimeType: payload.mimeType }),
          );
          return result;
        },
      ),
    }),
    selectors: {
      ...entityAdapter.getSelectors(),
    },
  });
  return slice;
};
